import type { GameState } from "../systems/GameState";
import type { InventoryManager } from "../systems/InventoryManager";
import type { EquipmentManager } from "../systems/EquipmentManager";
import type { MissionManager } from "../systems/MissionManager";
import type { HotspotData, NpcData, DialogueStep, ChapterData, LockoutExtra, ClassifyExtra, ErgoExtra, DecisionExtra, RiskAssessment, RiskControlOption, ControlTier } from "../data/content-types";

/** Resultado de una evaluación de riesgo (identificar + controlar). */
export interface AssessResult {
  score: number;                    // 0-5
  grade: "best" | "ok" | "bad";     // calidad del control elegido
  tier: ControlTier;
  identifyTries: number;
}

const TIER_LABEL: Record<ControlTier, string> = {
  eliminar: "Eliminar / sustituir",
  colectiva: "Protección colectiva",
  organizativa: "Medida organizativa",
  epi: "Equipo de protección individual",
  ninguna: "Ninguna medida",
};
import { ITEMS, itemName } from "../data/items";
import { signsBlock, signSvg } from "./signage";
import { Sfx } from "../systems/Sfx";

type El = HTMLElement;

function el(tag: string, cls = "", html?: string): El {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (html != null) n.innerHTML = html;
  return n;
}

const LOCKER_EPIS = ["casco", "gafas", "auditiva", "mascarilla", "chaleco", "guantes", "botas", "arnes"];

const ICON_HELMET = `<svg viewBox="0 0 24 24" width="18" height="18"><path d="M4 16h16v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill="#f0a91b"/><path d="M4 16c0-5 3-9 8-9s8 4 8 9" fill="none" stroke="#c9830a" stroke-width="2"/><path d="M12 7V4" stroke="#c9830a" stroke-width="2" stroke-linecap="round"/></svg>`;
const ICON_BAG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#241d14" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8h12l1 12H5z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>`;
const ICON_BOOK = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#241d14" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4h13a1 1 0 0 1 1 1v14H6a1 1 0 0 1-1-1z"/><path d="M8 4v15M11 9h5M11 13h5"/></svg>`;

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Pictograma GHS (rombo rojo + símbolo) para la clasificación química. */
function ghsPicto(type: string, size = 52): string {
  const sym: Record<string, string> = {
    inflamable: `<path d="M24 12c2 5 7 6 5 12-1 3-4 4-4 7a5 5 0 0 1-9-1c0-4 5-5 3-11 4 1 5 4 4 7 3-2 2-7-1-11-1-2 1-3 2-3z" fill="#241d14"/>`,
    comburente: `<circle cx="24" cy="26" r="8" fill="none" stroke="#241d14" stroke-width="3"/><path d="M18 12c1 3 4 3 3 7-1 2-3 2-3 4 3 1 5-1 5-4 2 2 1 5-1 7 4 0 6-3 6-6 0-4-4-5-4-8z" fill="#241d14"/>`,
    corrosivo: `<path d="M10 16l7 3-3 6zM32 15l-7 4 3 6z" fill="#241d14"/><path d="M13 24l5 8M31 23l-5 9" stroke="#241d14" stroke-width="2"/><path d="M12 34h24" stroke="#241d14" stroke-width="2.5"/>`,
    toxico: `<circle cx="24" cy="21" r="8" fill="#241d14"/><circle cx="21" cy="20" r="1.8" fill="#fff"/><circle cx="27" cy="20" r="1.8" fill="#fff"/><path d="M15 32l18 6M33 32l-18 6" stroke="#241d14" stroke-width="3" stroke-linecap="round"/>`,
  };
  return `<svg viewBox="0 0 48 48" width="${size}" height="${size}"><rect x="9" y="9" width="30" height="30" transform="rotate(45 24 24)" fill="#fff" stroke="#d21e1e" stroke-width="4"/>${sym[type] ?? ""}</svg>`;
}

/**
 * Capa de interfaz en DOM sobre el canvas de Phaser. Además de HUD,
 * hace de modo accesible (teclado/lector) que se conserva del clásico.
 */
export class UI {
  private root: El;
  private hud: El;
  private objectiveBox: El;
  private objectiveList: El;
  private secBar: El;
  private secNum: El;
  private modalLayer: El;
  private toastStack: El;

  constructor(
    private state: GameState,
    private inv: InventoryManager,
    private equip: EquipmentManager,
  ) {
    injectStyles();
    this.root = el("div", "epi-ui");
    // se ancla al escenario del juego (no al viewport) para no tapar el menú lateral
    (document.getElementById("stage") ?? document.body).appendChild(this.root);

    this.hud = el("div", "epi-hud");

    // Objetivos con aspecto de "parte de obra" (clip + papel)
    this.objectiveBox = el("div", "epi-objectives");
    this.objectiveBox.appendChild(el("div", "epi-clip"));
    this.objectiveList = el("div", "epi-obj-list");
    this.objectiveBox.appendChild(this.objectiveList);
    this.hud.appendChild(this.objectiveBox);

    // Seguridad con icono de casco + barra segmentada
    const sec = el("div", "epi-sec");
    sec.innerHTML = `<span class="epi-sec-ico">${ICON_HELMET}</span><span class="epi-sec-label">Seguridad</span>`;
    const barWrap = el("span", "epi-sec-bar");
    this.secBar = el("i");
    barWrap.appendChild(this.secBar);
    sec.appendChild(barWrap);
    this.secNum = el("b", "", "0%");
    sec.appendChild(this.secNum);
    this.hud.appendChild(sec);

    // Herramientas como botones-icono con atajos
    const tools = el("div", "epi-tools");
    const bag = el("button", "epi-tool", `${ICON_BAG}<span class="epi-key">I</span>`);
    bag.title = "Mochila [I]";
    bag.onclick = () => this.showInventory();
    const nb = el("button", "epi-tool", `${ICON_BOOK}<span class="epi-key">J</span>`);
    nb.title = "Cuaderno [J]";
    nb.onclick = () => this.showNotebook();
    tools.append(bag, nb);
    this.hud.appendChild(tools);

    this.root.appendChild(this.hud);

    this.modalLayer = el("div", "epi-modal-layer");
    this.root.appendChild(this.modalLayer);

    this.toastStack = el("div", "epi-toast-stack");
    this.root.appendChild(this.toastStack);

    // clic suave en cualquier botón de la interfaz
    this.root.addEventListener("click", (e) => {
      if ((e.target as HTMLElement)?.closest?.(".epi-btn")) Sfx.click();
    });

    // atajos de teclado I / J (no chocan con el movimiento WASD)
    this.keyHandler = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === "m") { const muted = Sfx.toggleMute(); this.toast("Sonido", muted ? "Silenciado (M)" : "Activado (M)", "info"); return; }
      if (this.modalLayer.querySelector(".epi-backdrop")) return;
      if (k === "i") this.showInventory();
      else if (k === "j") this.showNotebook();
    };
    window.addEventListener("keydown", this.keyHandler);
  }

  private keyHandler: (e: KeyboardEvent) => void;

  // ---------- HUD ----------
  private doneObjIds = new Set<string>();
  private firstHud = true;
  private lastHazFound = -1;
  updateHud(mission: MissionManager, location: string, secValue: number, hazards?: { found: number; total: number }): void {
    this.objectiveList.innerHTML = "";
    const title = el("div", "epi-obj-title", mission.chapterTitle);
    this.objectiveList.appendChild(title);
    let justCompleted = false;
    for (const v of mission.visibleObjectives()) {
      const id = v.objective.id;
      const isNew = v.done && !this.firstHud && !this.doneObjIds.has(id);
      if (v.done) this.doneObjIds.add(id);
      if (isNew) justCompleted = true;
      const row = el("div", "epi-obj" + (v.done ? " done" : "") + (isNew ? " just" : ""));
      const mark = el("span", "epi-obj-mark", v.done ? "✓" : "○");
      const txt = el("span", "epi-obj-txt", v.progress ? `${v.objective.label} (${v.progress})` : v.objective.label);
      row.append(mark, txt);
      this.objectiveList.appendChild(row);
    }
    if (hazards && hazards.total > 0) {
      const grew = hazards.found > this.lastHazFound && this.lastHazFound >= 0;
      const hz = el("div", "epi-haz" + (grew ? " just" : ""), `<span>Riesgos detectados</span><b>${hazards.found}/${hazards.total}</b>`);
      this.objectiveList.appendChild(hz);
      this.lastHazFound = hazards.found;
    }
    if (justCompleted) Sfx.tick();
    this.firstHud = false;
    void location;
    this.secBar.style.width = secValue + "%";
    this.secNum.textContent = secValue + "%";
  }

  // ---------- Modal genérico ----------
  private modal(): { sheet: El; close: () => void } {
    const backdrop = el("div", "epi-backdrop");
    const sheet = el("div", "epi-sheet");
    backdrop.appendChild(sheet);
    this.modalLayer.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });
    return { sheet, close };
  }

  // ---------- Inspección ----------
  showInspect(
    data: HotspotData,
    firstTime: boolean,
    extra?: { hazard?: { category: string; severity: number }; item?: string },
    action?: HotspotData["action"],
    onChoose?: (i: number) => void,
    onMeasure?: () => void,
    assess?: RiskAssessment,
    onAssess?: (r: AssessResult) => void,
  ): void {
    const { sheet, close } = this.modal();
    let measured = false;
    let lastResult: { why: string; ok: boolean } | undefined;
    // estado de la evaluación de riesgo
    let idTries = 0;
    let idDone = !assess;
    let idWrongHint = false;
    let control: RiskControlOption | null = null;
    // minijuego "aplica la medida" (pasos en orden)
    let applied = false;
    let applyOrder: number[] = [];
    let applyDone: number[] = [];
    let applyWrong = false;
    let applyLast = -1;

    const build = (result?: { why: string; ok: boolean }): void => {
      lastResult = result;
      sheet.innerHTML = "";
      sheet.appendChild(el("div", "epi-sheet-head", `<h2>${data.label}</h2>`));
      const body = el("div", "epi-sheet-body");
      body.appendChild(el("p", "", data.text));
      if (data.comment) body.appendChild(el("div", "epi-callout", `<b>EPI:</b> ${data.comment}`));
      if (extra?.hazard) {
        const sv = extra.hazard.severity;
        const b = el("div", "epi-callout", `<b>Riesgo detectado</b> — ${extra.hazard.category} · gravedad ${sv}/3`);
        b.style.borderColor = sv >= 3 ? "#b23a2a" : sv === 2 ? "#c9830a" : "#c9a20a";
        b.style.background = "#fff4ec";
        body.appendChild(b);
      }
      if (extra?.item) {
        const b = el("div", "epi-callout", `<b>A la mochila:</b> ${extra.item}`);
        b.style.borderColor = "#2f8f57"; b.style.background = "#eafaf0";
        body.appendChild(b);
      }
      if (data.signs?.length) {
        const w = el("div");
        w.innerHTML = signsBlock(data.signs);
        body.appendChild(w);
      }
      if (data.instrument) this.renderInstrument(body, data.instrument, measured, () => { measured = true; onMeasure?.(); build(lastResult); });
      if (firstTime) body.appendChild(el("p", "epi-saved", "Anotado en tu cuaderno."));

      // resultado de la microdecisión simple (capítulos aún no migrados)
      if (result) {
        const b = el("div", "epi-callout", result.why);
        b.style.borderColor = result.ok ? "#2f8f57" : "#b23a2a";
        b.style.background = result.ok ? "#eafaf0" : "#fff0ec";
        body.appendChild(b);
      }

      // ------ evaluación de riesgo (identificar → controlar → resultado) ------
      if (assess && !control && !idDone && idWrongHint) {
        const h = el("div", "epi-callout", `<b>EPI:</b> ${assess.identify.hint}`);
        h.style.borderColor = "#c9830a";
        body.appendChild(h);
      }
      if (assess && control) {
        const idOpt = assess.identify.options.find((o) => o.ok);
        if (idOpt) body.appendChild(this.evalTag(`Peligro: ${idOpt.text}`, "good"));
        const tone = control.grade === "best" ? "good" : control.grade === "ok" ? "warn" : "bad";
        body.appendChild(this.evalTag(`Medida elegida: ${TIER_LABEL[control.tier]}`, tone));
        const why = el("div", "epi-callout", control.why);
        why.style.borderColor = tone === "good" ? "#2f8f57" : tone === "warn" ? "#c9830a" : "#b23a2a";
        why.style.background = tone === "good" ? "#eafaf0" : tone === "bad" ? "#fff0ec" : "#fff7e4";
        body.appendChild(why);
        if (control.grade === "bad" && control.danger) {
          const d = el("div", "epi-callout", `<b>Cuasi-accidente:</b> ${control.danger}`);
          d.style.borderColor = "#b23a2a"; d.style.background = "#fbeae6";
          body.appendChild(d);
        }
        // minijuego "aplica la medida" (solo tras elegir la mejor)
        if (assess?.apply && control.grade === "best") {
          const ap = assess.apply;
          if (applied) {
            const s = el("div", "epi-callout epi-flash", `<b>Aplicada:</b> ${ap.success}`);
            s.style.borderColor = "#2f8f57"; s.style.background = "#eafaf0";
            body.appendChild(s);
          } else {
            body.appendChild(this.evalTag("3 · Aplica la medida — en orden", "warn"));
            body.appendChild(el("p", "epi-muted", ap.intro));
            const list = el("div", "epi-lock-steps" + (applyWrong ? " epi-shake" : ""));
            for (const i of applyOrder) {
              const isDone = applyDone.includes(i);
              const isNext = i === applyDone.length;
              const cell = el("button", "epi-lock-step" + (isDone ? " done" : "") + (isNext ? " next" : "") + (i === applyLast && isDone ? " epi-pop-me" : ""));
              cell.innerHTML = `<span class="epi-lock-n">${isDone ? applyDone.indexOf(i) + 1 : "·"}</span><span>${ap.steps[i]}</span>`;
              if (!isDone) cell.onclick = () => {
                if (i === applyDone.length) {
                  applyDone.push(i);
                  applyWrong = false;
                  applyLast = i;
                  if (applyDone.length === ap.steps.length) { applied = true; Sfx.done(); }
                  else Sfx.tick();
                } else {
                  applyDone = [];
                  applyWrong = true;
                  applyLast = -1;
                  Sfx.warn();
                }
                build();
              };
              list.appendChild(cell);
            }
            body.appendChild(list);
            if (applyWrong) {
              const w = el("div", "epi-callout", ap.wrong);
              w.style.borderColor = "#c9830a";
              body.appendChild(w);
            }
          }
        }
      }
      sheet.appendChild(body);

      // pregunta del paso de evaluación (va entre el cuerpo y el pie)
      let evalQ: El | null = null;
      const foot = el("div", "epi-sheet-foot");
      if (assess && !control) {
        if (!idDone) {
          evalQ = this.evalQuestion("1 · Identifica el riesgo", assess.identify.q);
          assess.identify.options.forEach((opt) => {
            const b = el("button", "epi-btn", opt.text);
            b.onclick = () => {
              if (opt.ok) { idDone = true; idWrongHint = false; }
              else { idTries++; idWrongHint = true; }
              build();
            };
            foot.appendChild(b);
          });
        } else {
          evalQ = this.evalQuestion("2 · Elige la medida (jerarquía preventiva)", assess.control.q);
          assess.control.options.forEach((opt) => {
            const b = el("button", "epi-btn", opt.text);
            b.onclick = () => {
              control = opt;
              const idPts = idTries === 0 ? 2 : 1;
              const ctrlPts = opt.grade === "best" ? 3 : opt.grade === "ok" ? 1 : 0;
              onAssess?.({ score: idPts + ctrlPts, grade: opt.grade, tier: opt.tier, identifyTries: idTries });
              // al elegir la MEJOR medida, hay que aplicarla paso a paso
              if (opt.grade === "best" && assess?.apply) {
                applyOrder = shuffle(assess.apply.steps.map((_, i) => i));
                applyDone = [];
                applyWrong = false;
              }
              build();
            };
            foot.appendChild(b);
          });
        }
      } else if (action && !result) {
        const q = el("div", "epi-callout", `<b>${action.q}</b>`);
        q.style.borderColor = "#c9830a";
        sheet.appendChild(q);
        action.options.forEach((opt, i) => {
          const b = el("button", "epi-btn", opt.text);
          b.onclick = () => { onChoose?.(i); build({ why: opt.why, ok: opt.ok }); };
          foot.appendChild(b);
        });
      } else if (assess && control && assess.apply && control.grade === "best" && !applied) {
        // en pleno minijuego: no se cierra hasta aplicar todos los pasos en orden
        foot.appendChild(el("span", "epi-muted", "Completa los pasos en orden para continuar."));
      } else {
        const ok = el("button", "epi-btn primary", "Entendido");
        ok.onclick = close;
        foot.appendChild(ok);
      }
      if (evalQ) sheet.appendChild(evalQ);
      sheet.appendChild(foot);
    };
    build();
  }

  /** Etiqueta compacta de estado dentro de la evaluación. */
  private evalTag(text: string, tone: "good" | "warn" | "bad"): El {
    const t = el("div", `epi-evaltag epi-evaltag-${tone}`, text);
    return t;
  }

  /** Cabecera de un paso de la evaluación (se inserta antes del pie). */
  private evalQuestion(step: string, q: string): El {
    return el("div", "epi-evalq", `<span class="epi-evalstep">${step}</span><b>${q}</b>`);
  }

  /** Panel de un instrumento de medida: el jugador pulsa y obtiene la lectura. */
  private renderInstrument(
    body: El,
    inst: NonNullable<HotspotData["instrument"]>,
    measured: boolean,
    onDone: () => void,
  ): void {
    const wrap = el("div", "epi-instr");
    const dev = el("div", "epi-instr-dev", signSvg(inst.device, 42));
    const panel = el("div", "epi-instr-panel");
    wrap.append(dev, panel);
    body.appendChild(wrap);

    const unit = inst.unit ? " " + inst.unit : "";
    const showResult = (): void => {
      panel.innerHTML = "";
      const screen = el("div", "epi-instr-screen" + (inst.danger ? " danger" : " ok"));
      if (inst.kind === "gauge") screen.textContent = `${inst.to ?? 0}${unit}`;
      else screen.textContent = inst.state === "sin_tension" ? "SIN TENSIÓN" : "¡TENSIÓN!";
      const verdict = el("div", "epi-instr-verdict" + (inst.danger ? " danger" : " ok"), inst.reading);
      panel.append(screen, verdict);
    };

    if (measured) { showResult(); return; }

    const screen = el("div", "epi-instr-screen");
    screen.textContent = inst.kind === "gauge" ? `--${unit}` : "· · ·";
    const btn = el("button", "epi-btn", inst.button);
    panel.append(screen, btn);

    btn.onclick = (): void => {
      btn.setAttribute("disabled", "");
      if (inst.kind === "gauge") {
        const from = inst.from ?? 0, to = inst.to ?? 0, dur = 950, t0 = performance.now();
        const tick = (): void => {
          if (!screen.isConnected) return;
          const k = Math.min(1, (performance.now() - t0) / dur);
          const eased = 1 - Math.pow(1 - k, 2);
          const jitter = k < 1 ? (Math.round((k * 37) % 3) - 1) : 0;
          screen.textContent = `${Math.round(from + (to - from) * eased) + jitter}${unit}`;
          if (k < 1) setTimeout(tick, 40);
          else onDone();
        };
        tick();
      } else {
        const dur = 720, t0 = performance.now();
        const blink = (): void => {
          if (!screen.isConnected) return;
          const k = (performance.now() - t0) / dur;
          if (k < 1) { screen.textContent = Math.floor(k * 7) % 2 ? "· · ·" : "· ? ·"; setTimeout(blink, 110); }
          else onDone();
        };
        blink();
      }
    };
  }

  // ---------- Diálogo ----------
  showDialogue(
    npc: NpcData,
    cb: { onFlag: (f: string) => void; onNoteFromStep: (n: DialogueStep) => void; onEnd: () => void },
  ): void {
    const { sheet, close } = this.modal();
    sheet.classList.add("epi-dialogue");
    let idx = 0;

    const render = () => {
      const step = npc.dialogue[idx];
      sheet.innerHTML = "";
      if (!step) {
        close();
        cb.onEnd();
        return;
      }
      const box = el("div", "epi-dlg-box");
      if ("choices" in step) {
        box.appendChild(el("div", "epi-dlg-name", "¿Qué le dices?"));
        const choices = el("div", "epi-dlg-choices");
        for (const ch of step.choices) {
          const b = el("button", "epi-btn", ch.text);
          b.onclick = () => {
            idx = ch.go;
            render();
          };
          choices.appendChild(b);
        }
        box.appendChild(choices);
      } else {
        const isEpi = step.speaker === "epi";
        box.appendChild(el("div", "epi-dlg-name" + (isEpi ? " epi" : ""), isEpi ? "EPI" : npc.name));
        box.appendChild(el("div", "epi-dlg-text", step.text));
        if (step.flag) cb.onFlag(step.flag);
        cb.onNoteFromStep(step);
        const next = el("div", "epi-dlg-next");
        const b = el("button", "epi-btn primary", step.end ? "Cerrar" : "Seguir");
        b.onclick = () => {
          if (step.end) {
            close();
            cb.onEnd();
          } else {
            idx++;
            render();
          }
        };
        next.appendChild(b);
        box.appendChild(next);
      }
      sheet.appendChild(box);
    };
    render();
  }

  // ---------- Taquilla / equipamiento ----------
  showLocker(_chapter: ChapterData, onConfirm: () => void): void {
    const { sheet, close } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", "<h2>Taquilla de EPIs</h2>"));
    const body = el("div", "epi-sheet-body");
    body.appendChild(el("p", "epi-muted", "Piensa en el trabajo y en el entorno: altura, golpes, materiales. Toca para equipar o quitar."));
    const grid = el("div", "epi-locker-grid");
    body.appendChild(grid);
    sheet.appendChild(body);

    const redraw = () => {
      grid.innerHTML = "";
      for (const id of LOCKER_EPIS) {
        const on = this.equip.hasEquipment(id);
        const cell = el("button", "epi-locker-item" + (on ? " on" : ""), itemName(id));
        cell.onclick = () => {
          if (on) {
            const slot = ITEMS[id].slot!;
            this.equip.unequip(slot);
          } else {
            this.equip.equip(id);
          }
          redraw();
        };
        grid.appendChild(cell);
      }
    };
    redraw();

    const foot = el("div", "epi-sheet-foot");
    const cancel = el("button", "epi-btn", "Cerrar");
    cancel.onclick = close;
    const done = el("button", "epi-btn primary", "Listo");
    done.onclick = () => {
      close();
      onConfirm();
    };
    foot.append(cancel, done);
    sheet.appendChild(foot);
  }

  // ---------- Consignación (LOTO) ----------
  showLockout(extra: LockoutExtra, onSuccess: () => void): void {
    const { sheet, close } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", `<h2>${extra.label}</h2>`));
    const body = el("div", "epi-sheet-body");
    body.appendChild(el("p", "epi-muted", "Haz los pasos <b>en el orden correcto</b>. Si te saltas uno, la máquina podría arrancar."));
    if (extra.signs?.length) {
      const w = el("div");
      w.innerHTML = signsBlock(extra.signs);
      body.appendChild(w);
    }
    const list = el("div", "epi-lock-steps");
    const result = el("div");
    body.append(list, result);
    sheet.appendChild(body);

    const correct = extra.steps.map((s) => s.id);
    const order = shuffle(correct.slice());
    let done: string[] = [];
    let lastDoneId: string | null = null;

    const redraw = () => {
      list.innerHTML = "";
      const nextId = correct[done.length];
      for (const id of order) {
        const step = extra.steps.find((s) => s.id === id)!;
        const isDone = done.includes(id);
        const cell = el("button", "epi-lock-step" + (isDone ? " done" : "") + (id === nextId ? " next" : "") + (id === lastDoneId ? " epi-pop-me" : ""));
        cell.innerHTML = `<span class="epi-lock-n">${isDone ? done.indexOf(id) + 1 : "·"}</span><span>${step.text}</span>`;
        if (!isDone) cell.onclick = () => tryStep(id);
        list.appendChild(cell);
      }
    };

    const tryStep = (id: string) => {
      if (id === correct[done.length]) {
        done.push(id); lastDoneId = id;
        if (done.length === correct.length) { Sfx.done(); complete(); }
        else { Sfx.tick(); redraw(); }
      } else {
        done = []; lastDoneId = null; Sfx.warn();
        result.innerHTML = `<div class="epi-callout" style="border-color:#c9830a">${extra.wrong}</div>`;
        redraw();
        list.classList.remove("epi-shake"); void list.offsetWidth; list.classList.add("epi-shake");
      }
    };

    const foot = el("div", "epi-sheet-foot");
    const reset = el("button", "epi-btn", "Empezar de nuevo");
    reset.onclick = () => { done = []; result.innerHTML = ""; redraw(); };
    const cont = el("button", "epi-btn primary", "Continuar");
    cont.style.display = "none";
    cont.onclick = () => { close(); onSuccess(); };
    foot.append(reset, cont);
    sheet.appendChild(foot);

    const complete = () => {
      result.innerHTML = `<div class="epi-callout" style="border-color:#2f8f57;background:#eafaf0">${extra.success}</div>`;
      reset.style.display = "none";
      cont.style.display = "";
      redraw();
      list.classList.remove("epi-flash"); void list.offsetWidth; list.classList.add("epi-flash");
    };

    redraw();
  }

  // ---------- Clasificación química (pictogramas GHS) ----------
  showClassify(extra: ClassifyExtra, onSuccess: () => void): void {
    const { sheet, close } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", `<h2>${extra.label}</h2>`));
    const body = el("div", "epi-sheet-body");
    body.appendChild(el("p", "epi-muted", "Cada bote lleva su <b>pictograma</b>. Míralo y manda cada producto a su armario. Los incompatibles no comparten estante."));
    if (extra.signs?.length) {
      const w = el("div");
      w.innerHTML = signsBlock(extra.signs, "Pictogramas CLP/GHS");
      body.appendChild(w);
    }
    const grid = el("div", "epi-classify");
    const result = el("div");
    body.append(grid, result);
    sheet.appendChild(body);

    const pick: Record<string, string> = {};

    const redraw = () => {
      grid.innerHTML = "";
      for (const pr of extra.products) {
        const row = el("div", "epi-prod");
        row.innerHTML = `<div class="epi-picto">${ghsPicto(pr.picto)}</div><div class="epi-pn"><b>${pr.name}</b><span>${pr.hint}</span></div>`;
        const seg = el("div", "epi-fam");
        for (const f of extra.families) {
          const b = el("button", pick[pr.id] === f.key ? "sel" : "", f.label);
          b.onclick = () => { pick[pr.id] = f.key; redraw(); };
          seg.appendChild(b);
        }
        row.appendChild(seg);
        grid.appendChild(row);
      }
    };
    redraw();

    const foot = el("div", "epi-sheet-foot");
    const check = el("button", "epi-btn", "Comprobar");
    const cont = el("button", "epi-btn primary", "Continuar");
    cont.style.display = "none";
    check.onclick = () => {
      if (extra.products.some((p) => pick[p.id] == null)) {
        Sfx.warn();
        result.innerHTML = `<div class="epi-callout" style="border-color:#c9830a">Falta clasificar algún producto.</div>`;
        return;
      }
      const ok = extra.products.every((p) => pick[p.id] === p.fam);
      if (ok) {
        Sfx.done();
        result.innerHTML = `<div class="epi-callout epi-flash" style="border-color:#2f8f57;background:#eafaf0">${extra.success}</div>`;
        check.style.display = "none";
        cont.style.display = "";
      } else {
        Sfx.warn();
        result.innerHTML = `<div class="epi-callout epi-shake" style="border-color:#c9830a">${extra.wrong}</div>`;
      }
    };
    cont.onclick = () => { close(); onSuccess(); };
    foot.append(check, cont);
    sheet.appendChild(foot);
  }

  // ---------- Ajuste ergonómico ----------
  showErgo(extra: ErgoExtra, onSuccess: () => void): void {
    const { sheet, close } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", `<h2>${extra.label}</h2>`));
    const body = el("div", "epi-sheet-body");
    body.appendChild(el("p", "epi-muted", "Coloca cada elemento en su punto. La comodidad sube cuando la postura es neutra."));
    const meter = el("div", "epi-ergo-meter");
    meter.innerHTML = `<span>Comodidad</span><span class="epi-ergo-bar"><i></i></span><b>0%</b>`;
    const comment = el("div", "epi-ergo-comment", "—");
    const rows = el("div", "epi-ergo-rows");
    const result = el("div");
    body.append(meter, comment, rows, result);
    sheet.appendChild(body);

    const pick: Record<string, number> = {};
    extra.params.forEach((p) => (pick[p.id] = p.start));

    const comfort = () => Math.round((extra.params.filter((p) => pick[p.id] === p.target).length / extra.params.length) * 100);

    const redraw = () => {
      rows.innerHTML = "";
      for (const p of extra.params) {
        const good = pick[p.id] === p.target;
        const row = el("div", "epi-ergo-row" + (good ? " good" : ""));
        const dots = p.labels.map((_, i) => `<i class="${i === pick[p.id] ? "on" : ""}"></i>`).join("");
        row.innerHTML = `<div class="epi-ergo-l"><b>${p.label}</b><span>${p.labels[pick[p.id]]}</span></div><div class="epi-ergo-step"><button data-d="-1">−</button><div class="epi-ergo-dots">${dots}</div><button data-d="1">+</button></div>`;
        row.querySelectorAll<HTMLButtonElement>(".epi-ergo-step button").forEach((b) =>
          (b.onclick = () => {
            const prev = pick[p.id];
            pick[p.id] = Math.max(0, Math.min(p.labels.length - 1, pick[p.id] + Number(b.dataset.d)));
            if (pick[p.id] !== prev) Sfx.tick();
            redraw();
          }),
        );
        rows.appendChild(row);
      }
      const c = comfort();
      (meter.querySelector("i") as HTMLElement).style.width = c + "%";
      (meter.querySelector("b") as HTMLElement).textContent = c + "%";
      comment.textContent = c >= 100 ? "“Ahora sí. El cuello y las muñecas lo notan.”" : c >= 60 ? "“Voy mejor... pero aún fuerzo la postura.”" : "“Así no hay quien aguante un turno.”";
    };
    redraw();

    const foot = el("div", "epi-sheet-foot");
    const check = el("button", "epi-btn", "Comprobar postura");
    const cont = el("button", "epi-btn primary", "Continuar");
    cont.style.display = "none";
    check.onclick = () => {
      if (comfort() >= 100) {
        Sfx.done();
        result.innerHTML = `<div class="epi-callout epi-flash" style="border-color:#2f8f57;background:#eafaf0">${extra.success}</div>`;
        check.style.display = "none";
        cont.style.display = "";
      } else {
        Sfx.warn();
        result.innerHTML = `<div class="epi-callout epi-shake" style="border-color:#c9830a">${extra.wrong}</div>`;
      }
    };
    cont.onclick = () => { close(); onSuccess(); };
    foot.append(check, cont);
    sheet.appendChild(foot);
  }

  // ---------- Decisión (mecánica social) ----------
  showDecide(extra: DecisionExtra, onSuccess: () => void): void {
    const { sheet, close } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", `<h2>${extra.label}</h2>`));
    const body = el("div", "epi-sheet-body");
    if (extra.intro) body.appendChild(el("p", "epi-muted", extra.intro));
    const list = el("div", "epi-decide");
    const result = el("div");
    body.append(list, result);
    sheet.appendChild(body);

    const pick: Record<number, number> = {};
    let revealed = false;

    const redraw = () => {
      list.innerHTML = "";
      extra.dilemas.forEach((d, i) => {
        const sel = pick[i];
        const chosenOk = sel != null && d.options[sel].ok;
        const card = el("div", "epi-dec-card" + (revealed && sel != null ? (chosenOk ? " ok" : " no") : ""));
        card.appendChild(el("div", "epi-dec-q", `<span class="epi-dec-n">${i + 1}</span>${d.q}`));
        const opts = el("div", "epi-dec-opts");
        d.options.forEach((o, oi) => {
          const b = el("button", sel === oi ? "sel" : "", o.text);
          b.onclick = () => { pick[i] = oi; revealed = false; result.innerHTML = ""; redraw(); };
          opts.appendChild(b);
        });
        card.appendChild(opts);
        if (revealed && sel != null) card.appendChild(el("div", "epi-dec-why " + (chosenOk ? "good" : "bad"), d.options[sel].why));
        list.appendChild(card);
      });
    };
    redraw();

    const foot = el("div", "epi-sheet-foot");
    const check = el("button", "epi-btn", "Comprobar");
    const cont = el("button", "epi-btn primary", "Continuar");
    cont.style.display = "none";
    check.onclick = () => {
      if (extra.dilemas.some((_, i) => pick[i] == null)) {
        Sfx.warn();
        result.innerHTML = `<div class="epi-callout" style="border-color:#c9830a">Falta decidir en algún punto. No hay respuesta 'neutra'.</div>`;
        return;
      }
      revealed = true;
      const allOk = extra.dilemas.every((d, i) => d.options[pick[i]].ok);
      redraw();
      if (allOk) {
        Sfx.done();
        result.innerHTML = `<div class="epi-callout epi-flash" style="border-color:#2f8f57;background:#eafaf0">${extra.success}</div>`;
        check.style.display = "none";
        cont.style.display = "";
      } else {
        Sfx.warn();
        result.innerHTML = `<div class="epi-callout epi-shake" style="border-color:#c9830a">${extra.wrong}</div>`;
      }
    };
    cont.onclick = () => { close(); onSuccess(); };
    foot.append(check, cont);
    sheet.appendChild(foot);
  }

  // ---------- Consecuencia ----------
  showConsequence(title: string, bodyHtml: string, buttonText: string, onClose: () => void): void {
    const { sheet, close } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", `<h2>${title}</h2>`));
    sheet.appendChild(el("div", "epi-sheet-body", bodyHtml));
    const foot = el("div", "epi-sheet-foot");
    const b = el("button", "epi-btn primary", buttonText);
    b.onclick = () => {
      close();
      onClose();
    };
    foot.appendChild(b);
    sheet.appendChild(foot);
  }

  // ---------- Mochila ----------
  showInventory(): void {
    const { sheet } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", "<h2>Mochila</h2>"));
    const body = el("div", "epi-sheet-body");
    const grid = el("div", "epi-inv-grid");
    const ids = this.inv.list();
    if (ids.length === 0) body.appendChild(el("p", "epi-muted", "Aún no llevas nada."));
    for (const id of ids) grid.appendChild(el("div", "epi-inv-cell", `<b>${itemName(id)}</b><span>${ITEMS[id]?.desc ?? ""}</span>`));
    body.appendChild(grid);
    sheet.appendChild(body);
    this.closeFoot(sheet);
  }

  // ---------- Cuaderno ----------
  showNotebook(): void {
    const { sheet } = this.modal();
    sheet.appendChild(el("div", "epi-sheet-head", "<h2>Cuaderno de EPI</h2>"));
    const body = el("div", "epi-sheet-body");
    let any = false;
    for (const [section, notes] of Object.entries(this.state.notebook)) {
      if (notes.length === 0) continue;
      any = true;
      body.appendChild(el("h3", "epi-nb-sec", section));
      for (const n of notes) body.appendChild(el("div", "epi-nb-entry", `<b>${n.heading}</b><span>${n.body}</span>`));
    }
    if (!any) body.appendChild(el("p", "epi-muted", "Explora, habla y descubre para llenar el cuaderno."));
    sheet.appendChild(body);
    this.closeFoot(sheet);
  }

  private closeFoot(sheet: El): void {
    const foot = el("div", "epi-sheet-foot");
    const b = el("button", "epi-btn", "Cerrar");
    b.onclick = () => sheet.closest(".epi-backdrop")?.remove();
    foot.appendChild(b);
    sheet.appendChild(foot);
  }

  /** Retira el overlay del DOM (al cambiar de escena). */
  destroy(): void {
    window.removeEventListener("keydown", this.keyHandler);
    this.root.remove();
  }

  // ---------- Toast ----------
  toast(title: string, msg: string, tone: "info" | "good" | "warn" | "unlock" = "info"): void {
    const t = el("div", `epi-toast epi-toast-${tone}`, `<b>${title}</b><span>${msg}</span>`);
    this.toastStack.appendChild(t);
    // entra deslizando
    requestAnimationFrame(() => t.classList.add("in"));
    const life = tone === "unlock" ? 4200 : 3000;
    setTimeout(() => {
      t.style.opacity = "0";
      t.style.transform = "translateX(120%)";
      setTimeout(() => t.remove(), 400);
    }, life);
    // no dejar que se acumulen infinitos (máx. 4 visibles)
    while (this.toastStack.children.length > 4) this.toastStack.firstElementChild?.remove();
  }
}

function injectStyles(): void {
  if (document.getElementById("epi-ui-styles")) return;
  const css = `
  .epi-ui{position:absolute;inset:0;pointer-events:none;font-family:"Segoe UI",system-ui,sans-serif;color:#241d14;z-index:20}
  .epi-ui button{font-family:inherit;color:#241d14}
  .epi-lock-step,.epi-lock-step span{color:#241d14}
  .epi-hud{position:absolute;top:14px;left:14px;right:14px;display:flex;align-items:flex-start;gap:10px;pointer-events:none}
  .epi-objectives{position:relative;pointer-events:auto;background:linear-gradient(#f7efdb,#efe3c6);border:1px solid #b7a07a;border-radius:4px;padding:16px 14px 12px;box-shadow:0 10px 26px rgba(40,28,10,.35);max-width:300px;transform:rotate(-.5deg)}
  .epi-clip{position:absolute;top:-9px;left:50%;transform:translateX(-50%);width:52px;height:16px;background:linear-gradient(#c9cdd3,#9aa0a8);border:1px solid #7c828b;border-radius:5px;box-shadow:0 2px 3px rgba(0,0,0,.3)}
  .epi-obj-title{font-weight:800;font-size:14px;margin-bottom:6px;letter-spacing:.03em;text-transform:uppercase;color:#3a2d1a;border-bottom:2px solid #cbb891;padding-bottom:4px}
  .epi-obj{display:flex;gap:7px;font-size:12.5px;line-height:1.55;color:#5c5140;align-items:baseline}
  .epi-obj.done{color:#2f8f57;text-decoration:line-through;text-decoration-color:rgba(47,143,87,.5)}
  .epi-obj-mark{font-weight:800;font-size:13px}
  .epi-haz{display:flex;justify-content:space-between;align-items:center;margin-top:8px;padding:5px 8px;border-radius:7px;background:#f3e6c6;border:1px solid #cbb891;font-size:11.5px;color:#5c5140;letter-spacing:.02em}
  .epi-haz b{color:#8a5a10;font-size:13px;font-variant-numeric:tabular-nums}
  .epi-stats{display:grid;grid-template-columns:1fr 1fr;gap:.5rem;margin:.7rem 0}
  .epi-stat{display:flex;flex-direction:column;gap:2px;background:#e6d7ba;border:1px solid #cbb891;border-radius:9px;padding:.5rem .7rem}
  .epi-stat span{font-size:.72rem;color:#5c5140;text-transform:uppercase;letter-spacing:.04em}
  .epi-stat b{font-size:1.05rem;color:#3a2d1a;font-variant-numeric:tabular-nums}
  .epi-sec{pointer-events:auto;margin-left:auto;background:#20242b;border:1px solid #3a3f47;border-radius:10px;padding:8px 12px;display:flex;align-items:center;gap:8px;font-weight:700;font-size:12.5px;color:#f4ead0;box-shadow:0 8px 22px rgba(0,0,0,.3)}
  .epi-sec-ico{display:flex}
  .epi-sec-label{letter-spacing:.03em;text-transform:uppercase;font-size:11px;opacity:.85}
  .epi-sec-bar{width:110px;height:12px;border-radius:6px;background:#0d0f12;border:1px solid #3a3f47;overflow:hidden;display:inline-block;position:relative}
  .epi-sec-bar>i{display:block;height:100%;background:linear-gradient(90deg,#b23a2a,#f0a91b 55%,#2f8f57);transition:width .5s}
  .epi-tools{pointer-events:auto;display:flex;gap:8px}
  .epi-tool{position:relative;width:44px;height:44px;border-radius:10px;border:2px solid #8a785a;background:#f6eeda;box-shadow:0 4px 0 rgba(90,70,35,.3);cursor:pointer;display:flex;align-items:center;justify-content:center}
  .epi-tool:hover{background:#fff6e2}
  .epi-tool:active{transform:translateY(2px);box-shadow:0 2px 0 rgba(90,70,35,.3)}
  .epi-key{position:absolute;bottom:-6px;right:-6px;background:#241d14;color:#f0a91b;font-size:10px;font-weight:800;width:16px;height:16px;border-radius:5px;display:flex;align-items:center;justify-content:center;border:1px solid #f0a91b}
  .epi-btn{padding:.62em 1.05em;border-radius:11px;border:2px solid #8a785a;background:#f6eeda;color:#241d14;font-weight:700;font-size:.95rem;box-shadow:0 3px 0 rgba(90,70,35,.32);cursor:pointer;transition:transform .08s ease,box-shadow .08s ease,background .15s}
  .epi-btn:hover{background:#fff6e2;transform:translateY(-1px);box-shadow:0 4px 0 rgba(90,70,35,.32)}
  .epi-btn:active{transform:translateY(2px);box-shadow:0 1px 0 rgba(90,70,35,.32)}
  .epi-btn:focus-visible{outline:3px solid #f0a91b;outline-offset:2px}
  .epi-btn.primary{background:linear-gradient(#f6b62e,#f0a91b);border-color:#c9830a;color:#3a2600}
  .epi-btn.primary:hover{background:linear-gradient(#f9c145,#f4b224)}
  @media(prefers-reduced-motion:reduce){.epi-btn{transition:none}}
  .epi-modal-layer{position:absolute;inset:0}
  .epi-backdrop{position:absolute;inset:0;background:rgba(30,20,8,.55);display:flex;align-items:center;justify-content:center;padding:16px;pointer-events:auto}
  .epi-sheet{background:#efe3cb;border:2px solid #8a785a;border-radius:14px;box-shadow:0 18px 50px rgba(0,0,0,.5);width:min(620px,100%);max-height:92%;display:flex;flex-direction:column;overflow:hidden}
  .epi-sheet-head{padding:.7rem 1rem;border-bottom:2px solid #c9b78f;background:#e6d7ba}
  .epi-sheet-head h2{margin:0;font-size:1.1rem}
  .epi-sheet-body{padding:1rem;overflow:auto;line-height:1.5}
  .epi-sheet-foot{padding:.7rem 1rem;border-top:2px solid #c9b78f;background:#e6d7ba;display:flex;gap:.5rem;justify-content:flex-end}
  .epi-callout{border-left:4px solid #f0a91b;background:#fff7e4;padding:.5rem .7rem;border-radius:0 8px 8px 0;margin:.5rem 0}
  .epi-saved{color:#124b46;font-weight:700;font-size:.85rem}
  .epi-signhead{font-weight:800;font-size:.7rem;letter-spacing:.05em;text-transform:uppercase;color:#5c5140;margin:.7rem 0 .35rem}
  .epi-signrow{display:flex;flex-wrap:wrap;gap:.55rem;margin:.1rem 0 .5rem}
  .epi-sign{display:flex;flex-direction:column;align-items:center;width:92px;text-align:center;gap:.28rem;margin:0}
  .epi-sign-ic{width:46px;height:46px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px rgba(40,28,10,.25))}
  .epi-sign figcaption{font-size:.66rem;line-height:1.22;color:#5c5140}
  .epi-instr{display:flex;gap:.7rem;align-items:stretch;margin:.6rem 0 .3rem;border:2px solid #8a785a;border-radius:12px;background:#20242b;padding:.6rem .7rem}
  .epi-instr-dev{flex:0 0 auto;width:46px;display:flex;align-items:center;justify-content:center;background:#2b3038;border-radius:8px;padding:.2rem}
  .epi-instr-panel{flex:1;display:flex;flex-direction:column;gap:.45rem;justify-content:center}
  .epi-instr-screen{font-family:"Consolas",ui-monospace,monospace;font-weight:800;font-size:1.5rem;letter-spacing:.06em;color:#93e07a;background:#0d1510;border:1px solid #37503a;border-radius:6px;padding:.15rem .5rem;text-align:center;font-variant-numeric:tabular-nums}
  .epi-instr-screen.danger{color:#ff6a5a;border-color:#5a2a26}
  .epi-instr-screen.ok{color:#93e07a}
  .epi-instr .epi-btn{align-self:flex-start}
  .epi-instr-verdict{font-size:.82rem;line-height:1.35;color:#e7dcc4;padding:.35rem .5rem;border-radius:7px;border-left:3px solid #f0a91b;background:rgba(255,255,255,.06)}
  .epi-instr-verdict.danger{border-left-color:#ff6a5a}
  .epi-instr-verdict.ok{border-left-color:#7ad39a}
  .epi-evalq{margin:.2rem 0 0;padding:.55rem .7rem;border-top:2px solid #c9b78f;background:#e6d7ba;display:flex;flex-direction:column;gap:.15rem}
  .epi-evalstep{font-size:.66rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#8a6a2a}
  .epi-evalq b{font-size:.95rem}
  .epi-evaltag{display:inline-block;font-size:.78rem;font-weight:700;padding:.28rem .6rem;border-radius:8px;margin:.35rem .35rem 0 0}
  .epi-evaltag-good{background:#eafaf0;color:#1e5b38;border:1px solid #2f8f57}
  .epi-evaltag-warn{background:#fff7e4;color:#8a5a10;border:1px solid #c9830a}
  .epi-evaltag-bad{background:#fbeae6;color:#7a2018;border:1px solid #b23a2a}
  .epi-muted{color:#5c5140}
  .epi-dialogue{width:min(720px,100%)}
  .epi-dlg-box{padding:1.15rem 1.25rem}
  .epi-dlg-name{font-weight:800;font-size:1.08rem;color:#124b46;margin-bottom:.35rem;letter-spacing:.01em}
  .epi-dlg-name.epi{color:#df5f2a}
  .epi-dlg-text{font-size:1.22rem;line-height:1.62;min-height:2.6em;color:#241d14}
  .epi-dlg-choices{display:flex;flex-direction:column;gap:.5rem;margin-top:.7rem}
  .epi-dlg-choices .epi-btn{font-size:1.05rem;padding:.7em 1.1em;text-align:left}
  .epi-dlg-next{display:flex;justify-content:flex-end;margin-top:.6rem}
  .epi-locker-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:.5rem;margin-top:.5rem}
  .epi-locker-item{padding:.7rem .5rem;border-radius:10px;border:2px solid #8a785a;background:#f6eeda;font-weight:700;cursor:pointer}
  .epi-locker-item.on{background:#eafaf0;border-color:#2f8f57;color:#1e5b38}
  .epi-lock-steps{display:flex;flex-direction:column;gap:.5rem;margin-top:.5rem}
  .epi-lock-step{display:flex;align-items:center;gap:.6rem;border:2px solid #8a785a;border-radius:10px;background:#f6eeda;padding:.55rem .7rem;text-align:left;font-weight:600;cursor:pointer;font-size:.9rem}
  .epi-lock-step:hover{background:#fff6e2}
  .epi-lock-step.next{border-color:#f0a91b;box-shadow:0 0 0 3px rgba(240,169,27,.2)}
  .epi-lock-step.done{border-color:#2f8f57;background:#eafaf0;cursor:default}
  .epi-lock-n{width:26px;height:26px;flex:0 0 auto;border-radius:7px;background:#e6d7ba;border:2px solid #8a785a;font-weight:800;display:flex;align-items:center;justify-content:center}
  .epi-lock-step.done .epi-lock-n{background:#2f8f57;border-color:#1e6b3f;color:#fff}
  @keyframes epi-pop{0%{transform:scale(.82)}55%{transform:scale(1.06)}100%{transform:scale(1)}}
  .epi-pop-me{animation:epi-pop .3s ease}
  @keyframes epi-shake{0%,100%{transform:translateX(0)}18%{transform:translateX(-7px)}36%{transform:translateX(7px)}54%{transform:translateX(-5px)}72%{transform:translateX(5px)}88%{transform:translateX(-2px)}}
  .epi-shake{animation:epi-shake .36s cubic-bezier(.36,.07,.19,.97)}
  @keyframes epi-flash{0%{box-shadow:0 0 0 0 rgba(47,143,87,.55)}100%{box-shadow:0 0 0 14px rgba(47,143,87,0)}}
  .epi-flash{animation:epi-flash .5s ease-out}
  @keyframes epi-obj-pop{0%{background:rgba(47,143,87,.4);transform:translateX(-5px)}100%{background:transparent;transform:none}}
  .epi-obj.just{animation:epi-obj-pop .85s ease;border-radius:6px}
  .epi-obj.just .epi-obj-mark{display:inline-block;animation:epi-pop .5s ease}
  @media(prefers-reduced-motion:reduce){.epi-pop-me,.epi-shake,.epi-flash,.epi-obj.just,.epi-obj.just .epi-obj-mark{animation:none}}
  .epi-classify{display:flex;flex-direction:column;gap:.6rem;margin-top:.5rem}
  .epi-prod{display:flex;align-items:center;gap:.75rem;border:2px solid #8a785a;border-radius:10px;background:#f6eeda;padding:.55rem .7rem;flex-wrap:wrap}
  .epi-picto{width:52px;height:52px;flex:0 0 auto}
  .epi-pn{flex:1;min-width:130px}
  .epi-pn b{display:block;font-size:.92rem}
  .epi-pn span{font-size:.78rem;color:#5c5140}
  .epi-fam{display:flex;flex-wrap:wrap;gap:.3rem}
  .epi-fam button{border:2px solid #8a785a;background:#f6eeda;border-radius:8px;padding:.32rem .6rem;font-weight:700;font-size:.8rem;cursor:pointer}
  .epi-fam button.sel{background:#241d14;color:#efe3cb;border-color:#241d14}
  .epi-ergo-meter{display:flex;align-items:center;gap:.6rem;margin:.2rem 0 .6rem;font-weight:700}
  .epi-ergo-bar{flex:1;height:14px;border-radius:7px;background:#e6d7ba;border:1px solid #8a785a;overflow:hidden}
  .epi-ergo-bar>i{display:block;height:100%;background:linear-gradient(90deg,#a83b2b,#f0a91b,#2f8f57);transition:width .35s}
  .epi-ergo-comment{font-style:italic;color:#124b46;font-size:.9rem;margin-bottom:.6rem;min-height:1.3em}
  .epi-ergo-rows{display:flex;flex-direction:column;gap:.5rem}
  .epi-ergo-row{display:flex;align-items:center;gap:.7rem;border:2px solid #8a785a;border-radius:10px;background:#f6eeda;padding:.5rem .7rem;flex-wrap:wrap}
  .epi-ergo-l{flex:1;min-width:150px}
  .epi-ergo-l b{display:block;font-size:.9rem}
  .epi-ergo-l span{font-size:.8rem;color:#5c5140}
  .epi-ergo-step{display:flex;align-items:center;gap:.4rem}
  .epi-ergo-step button{width:32px;height:32px;border:2px solid #8a785a;background:#f6eeda;border-radius:8px;font-weight:800;font-size:1.1rem;cursor:pointer}
  .epi-ergo-dots{display:flex;gap:.2rem}
  .epi-ergo-dots i{width:9px;height:9px;border-radius:50%;background:#e6d7ba;border:1px solid #8a785a}
  .epi-ergo-dots i.on{background:#241d14}
  .epi-ergo-row.good{border-color:#2f8f57;background:#eafaf0}
  .epi-ergo-row.good .epi-ergo-dots i.on{background:#2f8f57}
  .epi-decide{display:flex;flex-direction:column;gap:.6rem;margin-top:.4rem}
  .epi-dec-card{border:2px solid #8a785a;border-radius:12px;background:#f6eeda;padding:.6rem .7rem}
  .epi-dec-q{font-weight:800;font-size:.92rem;margin-bottom:.4rem;display:flex;gap:.5rem;align-items:center}
  .epi-dec-n{width:24px;height:24px;flex:0 0 auto;border-radius:6px;background:#241d14;color:#efe3cb;display:flex;align-items:center;justify-content:center;font-size:.82rem}
  .epi-dec-opts{display:flex;flex-direction:column;gap:.35rem}
  .epi-dec-opts button{text-align:left;border:2px solid #8a785a;background:#f6eeda;border-radius:9px;padding:.45rem .6rem;font-weight:600;font-size:.88rem;cursor:pointer}
  .epi-dec-opts button.sel{border-color:#241d14;background:#241d14;color:#efe3cb}
  .epi-dec-card.ok .epi-dec-opts button.sel{border-color:#1e6b3f;background:#2f8f57}
  .epi-dec-card.no .epi-dec-opts button.sel{border-color:#7a1f16;background:#b23a2a;color:#fff}
  .epi-dec-why{font-size:.82rem;margin-top:.4rem;padding:.4rem .55rem;border-radius:8px;line-height:1.35}
  .epi-dec-why.bad{background:#fbeae6;color:#7a2018;border-left:3px solid #b23a2a}
  .epi-dec-why.good{background:#eafaf0;color:#1e5b38;border-left:3px solid #2f8f57}
  .epi-inv-grid,.epi-nb-entry{margin-top:.4rem}
  .epi-inv-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:.5rem}
  .epi-inv-cell,.epi-nb-entry{border:1px solid #8a785a;border-radius:8px;background:#f6eeda;padding:.5rem .6rem;display:flex;flex-direction:column}
  .epi-inv-cell span,.epi-nb-entry span{font-size:.82rem;color:#5c5140}
  .epi-nb-sec{margin:.8rem 0 .2rem;text-transform:capitalize;font-size:.95rem}
  .epi-toast-stack{pointer-events:none;position:absolute;right:12px;bottom:16px;display:flex;flex-direction:column;gap:8px;align-items:flex-end;max-width:330px;z-index:30}
  .epi-toast{pointer-events:none;background:#241d14;color:#efe3cb;border:2px solid #f0a91b;border-left-width:5px;border-radius:12px;padding:.55rem .8rem;display:flex;flex-direction:column;gap:2px;box-shadow:0 8px 22px rgba(0,0,0,.4);transition:opacity .4s,transform .4s;max-width:330px;opacity:0;transform:translateX(30px)}
  .epi-toast.in{opacity:1;transform:translateX(0)}
  .epi-toast b{color:#f0a91b;font-size:.72rem;text-transform:uppercase;letter-spacing:.05em}
  .epi-toast span{font-size:.86rem;line-height:1.32}
  .epi-toast-good{border-color:#2f8f57}.epi-toast-good b{color:#7fe0a6}
  .epi-toast-warn{border-color:#c9830a}.epi-toast-warn b{color:#f0b050}
  .epi-toast-unlock{border-color:#6a8ef0}.epi-toast-unlock b{color:#a9c0ff}
  @media(prefers-reduced-motion:reduce){.epi-toast{transition:opacity .4s}}
  @media(max-width:720px){.epi-hud{flex-wrap:wrap}.epi-sec-bar{width:80px}}
  `;
  const style = el("style");
  style.id = "epi-ui-styles";
  style.textContent = css;
  document.head.appendChild(style);
}
